import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, Observable, tap, throwError } from 'rxjs';
import {
  CreateMerchantProductParams,
  DeleteMerchantProductByIdResponse,
  GetMerchantProductByIdResponse, GetMerchantProductRequestParams,
  GetMerchantProductResponse, UpdateMerchantProductRequestParams,
  MerchantProductData
} from '../../pages/merchantProducts/merchantProducts.interface';
import { environment } from '../../../environments/environment';
import { toQueryString } from '../../shared/helpers/helpers';

@Injectable({
  providedIn: 'root'
})
export class MerchantProductsService {
  private apiUrl = environment.api + '/merchant-products';
  private tokenKey = environment.accessTokenName;

  constructor(private http: HttpClient) {}

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem(this.tokenKey)}`
    });
  }

  private handleResponse<T>(response: T & { code: number; message?: string }): T {
    if (response.code !== 0) {
      throw new Error(response.message);
    }
    return response;
  }

  private handleError(error: any): Observable<never> {
    console.error('API Error:', error);
    return throwError(() => new Error(error.error?.message || 'Error'));
  }

  getMerchantProductService(requestParams: GetMerchantProductRequestParams): Observable<GetMerchantProductResponse> {
    const url = this.apiUrl + '?' + toQueryString(requestParams);
    return this.http.get<GetMerchantProductResponse>(url, { headers: this.getHeaders() }).pipe(
      tap(response => this.handleResponse(response)), // ✅ Pakai arrow function
      catchError(error => this.handleError(error))   // ✅ Pakai arrow function
    );
  }

  getMerchantProductById(id: string): Observable<GetMerchantProductByIdResponse> {
    return this.http.get<GetMerchantProductByIdResponse>(`${this.apiUrl}/${id}`, { headers: this.getHeaders() }).pipe(
      tap(response => this.handleResponse(response)),
      catchError(error => this.handleError(error))
    );
  }

  deleteMerchantProductById(id: string): Observable<DeleteMerchantProductByIdResponse> {
    return this.http.delete<DeleteMerchantProductByIdResponse>(`${this.apiUrl}/${id}`, { headers: this.getHeaders() }).pipe(
      tap(response => this.handleResponse(response)),
      catchError(error => this.handleError(error))
    );
  }

  createMerchantProduct(merchantProductData: CreateMerchantProductParams): Observable<GetMerchantProductByIdResponse> {
    return this.http.post<GetMerchantProductByIdResponse>(this.apiUrl, merchantProductData, { headers: this.getHeaders() }).pipe(
      tap(response => this.handleResponse(response)),
      catchError(error => this.handleError(error))
    );
  }

  updateMerchantProduct(id: string, merchantProductData: UpdateMerchantProductRequestParams): Observable<GetMerchantProductByIdResponse> {
    return this.http.put<GetMerchantProductByIdResponse>(`${this.apiUrl}/${id}`, merchantProductData, { headers: this.getHeaders() }).pipe(
      tap(response => this.handleResponse(response)),
      catchError(error => this.handleError(error))
    );
  }
}
